import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './JourneySection.css';

const steps = [
  {
    title: 'Identify',
    description: 'We help you identify high-impact AI opportunities and build a step-by-step AI Transformation strategy to bring them to life.',
  },
  {
    title: 'Educate',
    description: 'We train and support your team with the right tools and know-how to embed AI across your entire organization.',
  },
  {
    title: 'Develop',
    description: 'We leverage our extensive experience and network to develop custom AI systems that are proven to move the needle inside your business.',
  },
];

const JourneySection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const isTransitioning = useRef(false);
  const touchStartY = useRef(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleWheel = (e) => {
      const rect = section.getBoundingClientRect();
      
      // Check if section is in viewport
      if (rect.top <= 0 && rect.bottom >= window.innerHeight) {
        const direction = e.deltaY > 0 ? 1 : -1;
        
        // Handle step transitions
        if (!isTransitioning.current) {
          if (direction > 0 && activeStep < steps.length - 1) {
            e.preventDefault();
            isTransitioning.current = true;
            setActiveStep(prev => prev + 1);
            setTimeout(() => { isTransitioning.current = false; }, 1000);
          } else if (direction < 0 && activeStep > 0) {
            e.preventDefault();
            isTransitioning.current = true;
            setActiveStep(prev => prev - 1);
            setTimeout(() => { isTransitioning.current = false; }, 1000);
          }
          // Let normal scroll happen if at boundaries
        } else {
          e.preventDefault();
        }
      }
    };

    const handleTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const rect = section.getBoundingClientRect();
      
      if (rect.top <= 0 && rect.bottom >= window.innerHeight) {
        const touchEndY = e.touches[0].clientY;
        const deltaY = touchStartY.current - touchEndY;
        
        if (Math.abs(deltaY) > 50 && !isTransitioning.current) {
          if (deltaY > 0 && activeStep < steps.length - 1) {
            e.preventDefault();
            isTransitioning.current = true;
            setActiveStep(prev => prev + 1);
            setTimeout(() => { isTransitioning.current = false; }, 1000);
          } else if (deltaY < 0 && activeStep > 0) {
            e.preventDefault();
            isTransitioning.current = true;
            setActiveStep(prev => prev - 1);
            setTimeout(() => { isTransitioning.current = false; }, 1000);
          }
        }
      }
    };

    // Add event listeners
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [activeStep]);

  return (
    <section id="journey" className="journey-section" ref={sectionRef}>
      <div className="journey-container">
        <div className="journey-text-content">
          <ul>
            {steps.map((step, index) => (
              <motion.li
                key={step.title}
                className={activeStep === index ? 'active' : ''}
                initial={{ opacity: 0.5 }}
                animate={{ 
                  opacity: activeStep === index ? 1 : 0.5,
                  scale: activeStep === index ? 1.05 : 1,
                  x: activeStep === index ? 10 : 0
                }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                onClick={() => setActiveStep(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setActiveStep(index);
                }}
              >
                {step.title}
              </motion.li>
            ))}
          </ul>
        </div>
        <div className="journey-visual-content">
          <motion.div 
            className="visual-element-container"
            animate={{ 
              rotate: activeStep * 120,
              scale: 1 + (activeStep * 0.1)
            }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <div className="visual-sphere"></div>
          </motion.div>
          <AnimatePresence mode="wait">
            <motion.div 
              key={activeStep}
              className="journey-step-description"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.4 }}
            >
              <h3>{steps[activeStep].title}</h3>
              <p>{steps[activeStep].description}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default JourneySection; 